import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import style from './style.module.scss';
import {makeStyles, TextFieldProps, withStyles} from '@material-ui/core';
import classNames from 'classnames';
import ReactFlagsSelect from 'react-flags-select';
import { CountriesWithCode } from 'external-services/phone-number-service/country';
import PhoneIcon from '@material-ui/icons/Phone';
import MuiTextField from '@material-ui/core/TextField';
import PhoneNumberService from 'external-services/phone-number-service/index';
import { phoneNumberTypes } from 'external-services/phone-number-service/phoneNumber.types';
import { FlagPhonePickerStyle, IHandlers, IProps } from './types';
import { themeTypes } from 'shared-types/index';
import { debounce } from 'lodash-es';
import { useThrottle } from 'external-services/react-utils-service';
import DeprecatedAutoComplete from './DeprecatedAutoComplete';
import LocationService from 'external-services/location-service';

const NS = 'FlagPhonePickerReact';

export type Countries = Record<string, string>;

const useControlledInput = !LocationService.isIOS();

// export default function FlagPhonePickerReact(params: IProps & IHandlers) {
export default function FlagPhonePickerReact(props: IProps & IHandlers) {
  const {
    isPhoneIcon,
    value,
    handleBlur,
    handleChange,
    country,
    searchCustomer,
    customers,
    handleWholeFormChangeForAutocomplete,
    forceValidation,
    touched,
    errors,
    label,
    uiStyle,
    theme,
    maxLength,
    classPrefix,
    usePlaceholderForLabel,
    isDropDownListMoveUp,
    disabled,
    overrideStyles,
    name = 'phone',
    required,
    isNewCustomerForm,
    autoFocusOnLoad = true,
    shouldSkipAutoFocus,
    includeAutocomplete,
  } = props;
  const [selectedCountry, setSelectedCountry] = useState(country ? country : '');
  const [nationalNumber, setNationalNumber] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  const [numberMaxLength, setNumberMaxLength] = useState(25);

  const dropDownRef = useRef(null);
  const inputRef = useRef(null);

  const isDiaryNCF = uiStyle === FlagPhonePickerStyle.diary && isNewCustomerForm;

  const isDark = theme
    ? theme.type === themeTypes.dark || theme.type === themeTypes.outlinedDark
    : false;

  // defaults to true for all non-diary instances
  let isRequired = uiStyle !== FlagPhonePickerStyle.diary;

  // unless 'required' is specified, in which case it overrides the default
  if (required !== undefined) {
    isRequired = required;
  }

  useEffect(() => {
    country !== selectedCountry && setSelectedCountry(country);
  }, [country]);

  useEffect(() => {
    if (maxLength && uiStyle === FlagPhonePickerStyle.diary) {
      const countryData = PhoneNumberService.getCountryByCountryCode(selectedCountry);
      if (nationalNumber && PhoneNumberService.isNotPhoneNumberType(nationalNumber)) {
        setNumberMaxLength(maxLength);
      } else {
        if (countryData) {
          setNumberMaxLength(maxLength - countryData.dial_code.length);
        } else {
          setNumberMaxLength(maxLength);
        }
      }
    }
  }, [selectedCountry, nationalNumber]);

  const diaryNCFInputStyles = isDiaryNCF ? makeStyles<TextFieldProps>({
    root: {
      '& > div': {
        fontSize: 16,
        paddingLeft: 8,
        height: 42
      },
      '& > div:after': {
        borderBottom: 'none !important',
      },
      '& > div:before': {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12) !important',
      }
    }
  }) : null;

  const textInputClasses = diaryNCFInputStyles && diaryNCFInputStyles() || null;

  const TextField = useMemo(
    () =>
      withStyles({
        root: {
          '& > .MuiInputLabel-shrink': {
            transform: 'translate(0, 5px) scale(0.75)',
          },
          '& .MuiInputBase-input': {
            padding: '5px 0 6px',
            marginTop: uiStyle === FlagPhonePickerStyle.widget ? '5px' : '4px',
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInputBase-root`]:
            uiStyle === FlagPhonePickerStyle.diary
              ? {
                  fontSize: '16px',
                  paddingLeft: isNewCustomerForm ? '8px' : '0px',
                  height: isNewCustomerForm ? '42px' : 'auto'
                }
              : {},
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiFormLabel-root`]:
            uiStyle === FlagPhonePickerStyle.diary
              ? {
                  color: 'rgba(0, 0, 0, 0.26)',
                }
              : null,
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInputLabel-shrink`]:
            uiStyle === FlagPhonePickerStyle.diary
              ? {
                  transform: 'translate(0, 5px) scale(0.75)',
                  color: 'rgba(0, 0, 0, 0.6)',
                }
              : null,
          '& .MuiFormHelperText-root': {},
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiFormHelperText-root`]: {
            fontFamily: 'Roboto, Helvetica Neue, sans-serif',
            letterSpacing: '0',
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInput-underline.Mui-error:after`]:
            uiStyle === FlagPhonePickerStyle.diary
              ? {
                  borderBottomColor: 'rgba(0, 0, 0, 0.03)',
                }
              : null,
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInput-underline:after`]: isNewCustomerForm
            ? {
                borderBottom: 'none',
              }
            : null,
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInput-underline:before`]: uiStyle === FlagPhonePickerStyle.diary
            ? {
              borderBottomColor: '#DFDFDF',
              }
            : null,
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiFormHelperText-root.Mui-error`]:
            uiStyle === FlagPhonePickerStyle.widget
              ? {
                  fontSize: '12px',
                }
              : null,
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiFormLabel-root.Mui-focused`]:
            uiStyle === FlagPhonePickerStyle.diary
              ? usePlaceholderForLabel
                ? {
                    color: 'rgba(0, 0, 0, 0.26)',
                  }
                : {
                    color: overrideStyles?.focusedColor,
                  }
              : null,
          '& label':
            uiStyle !== FlagPhonePickerStyle.widget
              ? {
                  zIndex: 10,
                  fontSize: '1.6rem',
                }
              : null,
          '& label + .MuiInput-formControl': {
            marginTop: uiStyle === FlagPhonePickerStyle.widget ? '13px' : '12px',
          },
        },
      })(MuiTextField),
    []
  );
  useEffect(() => {
    window.addEventListener('click', (e: any) => {
      if (e?.target?.id === 'rfs-btn') {
        const ulChild = dropDownRef.current?.children[0].children[1];
        if (ulChild) {
          const inputChild = ulChild.children[0].children[0];
          inputChild.focus();
        }
      }
    });
  }, []);

  const countries = CountriesWithCode.map((c) => c.code);
  const countryLabels: Countries = {};
  CountriesWithCode.forEach((c) => {
    countryLabels[c.code] = c.code + ' ' + c.dial_code;
  });

  const formatPhoneNumber = (phone: string, countryCode: string) => {
    // should be return country-dial-code-removed-number
    let label = countryLabels[countryCode]; // get country label ex: "AF +93"
    label = label.replace(countryCode + ' ', ''); // Remove country code from label ex: "+93"
    label = label + ' '; // Add space into label ex: "+93 "
    return phone ? phone.replace(label, '') : ''; // Remove label from phone number ex: "+61 2343 2343 2343" => "2343 2343 2343"
  };


  const checkCountryCode = (str: string): string => {
    if (str === '+1') {
      setSelectedCountry('US');
      handleChange({
        ...{},
        target: {
          name: 'country',
          value: 'US',
        },
      });
      return '';
    }
    const isExistedCode: {
      name: string;
      dial_code: string;
      code: string;
    } = PhoneNumberService.getCountryByDialCode(str);
    if (isExistedCode) {
      setSelectedCountry(isExistedCode.code);
      handleChange({
        ...{},
        target: {
          name: 'country',
          value: isExistedCode.code,
        },
      });
      return '';
    } else {
      return str;
    }
  };

  useEffect(() => {

    let timeout: NodeJS.Timeout;
    if (autoFocusOnLoad) {
      timeout = setTimeout(() => {
        if (inputRef.current && isNewCustomerForm
            && (!shouldSkipAutoFocus || (shouldSkipAutoFocus && !shouldSkipAutoFocus()))) {
          inputRef.current?.focus();
        }
      }, 1500);
    }


    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  const throttledChangeHandler = useThrottle(handleChange, 700);

  const helperText = useMemo(() => {
    return uiStyle !== FlagPhonePickerStyle.diary
      ? touched || value
        ? errors
        : ''
      : touched
      ? errors
      : ''
  }, [touched, value, errors]);

  const inputProps = useMemo(() => {
    return uiStyle === FlagPhonePickerStyle.diary && maxLength
      ? {
          maxLength: numberMaxLength,
          inputMode: 'numeric' as const // shows number keyboard on touch devices
        }
      : {
          inputMode: 'numeric' as const // shows number keyboard on touch devices
        }
  }, [uiStyle, maxLength, numberMaxLength]);

  const labelText = useMemo(() => {
    return usePlaceholderForLabel
      ? null
      : label ??
        (uiStyle !== FlagPhonePickerStyle.diary ? <span>Mobile</span> : 'Phone')
  }, [usePlaceholderForLabel, label, uiStyle]);

  const placeholderText = useMemo(() => {
    return usePlaceholderForLabel ? 'Phone' : '';
  }, [usePlaceholderForLabel]);

  const error = useMemo(() => {
    return uiStyle !== FlagPhonePickerStyle.diary
      ? (forceValidation && !value) || ((touched || !!value) && Boolean(errors))
      : Boolean(errors)
  }, [uiStyle, forceValidation, touched, value, errors]);

  return (
    <div
      tabIndex={-1}
      className={classNames({
        [style.root]: true,
        [style.isFocused]: isNewCustomerForm && isFocused,
        [style.containerNcf]: isNewCustomerForm,
      })}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      {isPhoneIcon ? (
        <div
          className={classNames({
            [style.phoneIcon]: true,
          })}
        >
          <PhoneIcon />
        </div>
      ) : null}
      <div
        ref={dropDownRef}
        className={classNames({
          [style.prefixContainer]: true,
          [style.prefixPaddingContainer]:
            uiStyle !== FlagPhonePickerStyle.diary &&
            ((forceValidation && !value) || ((touched || value) && Boolean(errors))),
          [style.wrapperContainer]: uiStyle === FlagPhonePickerStyle.diary && Boolean(errors),
          [style.darkMode]: isDark,
        })}
      >
        <ReactFlagsSelect
          searchable
          disabled={disabled}
          fullWidth
          placeholder={'Country Code'}
          countries={countries}
          customLabels={countryLabels}
          selected={selectedCountry}
          onSelect={(code) => {
            setSelectedCountry(code);
            setIsFocused(false);
            handleChange({
              ...{},
              target: {
                name: 'country',
                value: code,
              },
            });
          }}
          className={classNames({
            [style.prefixFlagContainer]: true,
            [style.prefixFlagContainerIsNcf]: isNewCustomerForm,
            [style.dropDownListUp]: isDropDownListMoveUp,
            [style.prefixDiaryPaddingContainer]:
              uiStyle === FlagPhonePickerStyle.diary && Boolean(errors) && !usePlaceholderForLabel,
            [style.whiteBackground]: disabled,
          })}
          selectButtonClassName={classNames({
            [style.prefixButtonContainer]: true,
            [style.isDiaryPrefixButtonContainer]: uiStyle === FlagPhonePickerStyle.diary,
          })}
          selectedSize={isNewCustomerForm ? 16 : 12}
          optionsSize={12}
        />
      </div>
      <div
        className={classNames({
          [style.phoneNumberInputContainer]: true,
          [style.isDiary]: uiStyle === FlagPhonePickerStyle.diary,
          [style.isDiaryNcf]: isDiaryNCF
        })}
      >
        {includeAutocomplete ? (
          <DeprecatedAutoComplete
            TextFieldComponent={TextField}
            labelText={labelText}
            value={value}
            inputProps={inputProps}
            textInputClasses={textInputClasses}
            disabled={disabled}
            customers={customers}
            placeholderText={placeholderText}
            name={name}
            helperText={helperText}
            isRequired={isRequired}
            error={error}
            country={country}
            handleChange={(evt) => {
              let str: string = evt.target.value;
              str = checkCountryCode(str.replace(/[^+0-9]/g, ''));
              const international: string = selectedCountry
                ? formatPhoneNumber(
                    PhoneNumberService.formatNumber(
                      str,
                      selectedCountry,
                      true,
                      phoneNumberTypes.INTERNATIONAL
                    ),
                    selectedCountry
                  )
                : '';

              setNationalNumber(selectedCountry && international ? international : str);
              const event = {
                ...evt,
                target: {
                  name,
                  value: selectedCountry && international ? international : str,
                },
              };
              handleChange(event);
              const codeData = PhoneNumberService.getCountryByCountryCode(selectedCountry);
              searchCustomer(event, codeData?.dial_code);
            }}
            handleBlur={handleBlur}
            handleWholeFormChangeForAutocomplete={handleWholeFormChangeForAutocomplete}

          />
        ) : (
          <TextField
            classes={textInputClasses}
            label={labelText}
            inputProps={inputProps}
            placeholder={placeholderText}
            name={name}
            required={isRequired}
            fullWidth
            /**
             * NBI-8604: DO NOT CHANGE THIS WITHOUT PERFORMANCE TESTING ON OLDER IPADS (up to 6 years old).
             * Using `value` makes it a controlled component, which is too slow in older iPads, but
             * ok for desktop browsers. It wasn't possible to detect older iPads though reliably, so
             * we're applying it to all iOS devices. This means non-numeric characters can be entered
             * (they will be ignored in the search though). However, it now defaults to the numeric 
             * keyboard which makes it less likely that letters will be entered. It is a bit of a 
             * compromise, but it's the best we can do until 2020-ish touch devices are no longer in use.
             */
            defaultValue={nationalNumber}
            onChange={(evt) => {
              let str: string = evt.target.value;
              str = checkCountryCode(str.replace(/[^+0-9]/g, ''));

              /**
               * NBI-8604: One of the caveats of not using a controlled input is that the country code
               * is not removed from the input when the country is changed. This is a workaround to
               * remove the country code from the input when the country is changed. Only affects iOS.
               */
              if (!useControlledInput && inputRef?.current) {
                const countryCode = PhoneNumberService.getCountryByCountryCode(selectedCountry);
                if (countryCode && str.indexOf(countryCode.dial_code) === 0) {
                  inputRef.current.value = str.substring(countryCode.dial_code.length);
                }
              }

              const international: string = selectedCountry
                ? formatPhoneNumber(
                    PhoneNumberService.formatNumber(
                      str,
                      selectedCountry,
                      true,
                      phoneNumberTypes.INTERNATIONAL
                    ),
                    selectedCountry
                  )
                : '';

              setNationalNumber(selectedCountry && international ? international : str);
              const event = {
                ...evt,
                target: {
                  name,
                  value: selectedCountry && international ? international : str,
                },
              };
              throttledChangeHandler(event);
            }}
            onBlur={handleBlur}
            error={error}
            inputRef={inputRef}
            helperText={helperText}
          />
        )}
      </div>
    </div>
  );
}
